import { useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/** Backend caps each image to ~2.5 MB; warn the user before submit. */
const MAX_IMAGE_BYTES = 2_500_000;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Image picker for the public tournament menu (left/right slots).
 * - Default mode: file upload → converts to data URL (base64) for the backend.
 * - Advanced mode: paste an https URL (collapsed by default).
 * - Shows a live preview and a clear "remove" action.
 *
 * Props:
 *   label    – field label / aria text
 *   value    – current value (data URL or absolute URL)
 *   onChange – (next: string) => void
 *   disabled – boolean
 *   onError  – (message: string) => void   (optional, surfaces file-too-big)
 */
export default function PublicMenuImageField({ label, value, onChange, disabled, onError }) {
  const inputRef = useRef(null);
  const [showUrlMode, setShowUrlMode] = useState(false);
  const hasValue = Boolean(value && String(value).trim());

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      onError?.(`${label}: la imagen supera 2,5 MB. Prueba otra más pequeña.`);
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      onChange(dataUrl);
    } catch {
      onError?.(`${label}: no se pudo leer la imagen.`);
    } finally {
      e.target.value = '';
    }
  };

  const handleClear = () => onChange('');

  return (
    <Stack spacing={1.25}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f3531' }}>
        {label}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.25,
          borderRadius: 2,
          border: '1px dashed rgba(15,53,49,0.25)',
          bgcolor: 'rgba(15,107,92,0.04)',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 84,
            height: 84,
            borderRadius: 1.5,
            bgcolor: 'rgba(15,53,49,0.06)',
            backgroundImage: hasValue ? `url("${String(value).replace(/"/g, '\\"')}")` : undefined,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            flexShrink: 0,
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<CloudUploadOutlinedIcon />}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: 'rgba(15,107,92,0.45)',
              color: '#0f6b5c',
            }}
          >
            {hasValue ? 'Cambiar imagen' : 'Subir imagen'}
          </Button>
          {hasValue ? (
            <Tooltip title="Quitar imagen">
              <span>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  disabled={disabled}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title={showUrlMode ? 'Ocultar opción de URL' : 'Pegar una URL en su lugar'}>
            <span>
              <IconButton
                size="small"
                onClick={() => setShowUrlMode((s) => !s)}
                disabled={disabled}
                sx={{ color: showUrlMode ? '#0f6b5c' : 'text.secondary' }}
              >
                <LinkOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handlePick}
        />
      </Box>

      {showUrlMode ? (
        <TextField
          size="small"
          fullWidth
          label="URL de imagen (avanzado)"
          placeholder="https://…"
          value={String(value || '').startsWith('data:') ? '' : value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          helperText="Si pegas una URL https, sustituye al archivo subido."
        />
      ) : null}
    </Stack>
  );
}
